<template>
  <v-slide-y-reverse-transition>
  <v-container class="progress-card px-0">
    <div
      v-if="showFooter"
      style="border-radius: 30px;"
      class="footerblue"
    >
      <v-row align="center" class="progress-card__row px-3 py-0">
        <v-col cols="2" class="d-flex justify-start px-2 py-1">
          <v-btn
            v-show="showPrevious"
            class="lightblue--text"
            icon
            @click="goPrevious()"
            id="previous-btn"
          >
            <v-icon size="32px">{{ chevronBack }}</v-icon>
          </v-btn>
        </v-col>

        <v-col class="py-1">
          <div v-if="content==='text'" class="text-center">
            <mrx-translation :token=barText></mrx-translation>
          </div>
          <div
            v-else-if="content==='step'"
            class="d-flex justify-center"
          >
            <div
              v-for="(step, index) in steps"
              :key=index
              class="progress-dot lightblue mx-2"
              :class="step ? '' : 'faded'"
            ></div>
          </div>
          <div v-else>
            Other Type
          </div>
        </v-col>

        <v-col cols="2" class="d-flex justify-end px-2 py-1">
          <v-slide-x-transition hide-on-leave>
              <v-btn
                v-show="showNext"
                class="lightblue--text"
                id="next-btn"
                icon
                :disabled="isOnboarding && !ready"
                @click="next"
              >
                <v-icon size="32px">{{ chevronForward }}</v-icon>
              </v-btn>
          </v-slide-x-transition>
        </v-col>
      </v-row>
    </div>
  </v-container>
  </v-slide-y-reverse-transition>
</template>

<script>
  import icons from '@/mixins/icons'
  import { mapActions, mapState as mapPiniaState } from 'pinia'
  import { useLocationStore } from '@/stores/location';
  import { useLaunchSetupStore } from '@/stores/LaunchSetup'
  import { usePreferenceStore } from '@/stores/preference';
  import { useNavigation } from '@/composables/useNavigation';

  export default {
    mixins: [icons],
    setup() {
      const { next, previous, ready, showPrevious } = useNavigation();
      return {
        navigateNext: next,
        navigatePrevious: previous,
        navigationReady: ready,
        navigationShowPrevious: showPrevious,
      };
    },
    methods: {
      next() {
        if (this.isOnboarding && !this.ready) return
        switch(this.$route.name) {
          case 'Setup':
            if (this.ready) {
              this.$router.push(this.launchSetupNextRoute(this.$route.params.group, {
                returnToSummary: Boolean(this.$route.query.focus),
              }))
            }
            break;
          case 'Location':
            this.locationNext(this.navigateNext);
            break;
          default:
            this.navigateNext();
            break;
        }
      },
      goPrevious() {
        if (this.$route.name === 'Setup') {
          const route = this.launchSetupPreviousRoute(this.$route.params.group, {
            includeLanguage: this.isOnboarding,
          })
          if (route) this.$router.push(route)
          return
        }
        this.navigatePrevious()
      },
      ...mapActions(useLocationStore, { locationNext: 'next' }),
      ...mapActions(useLaunchSetupStore, {
        launchSetupNextRoute: 'nextRoute',
        launchSetupPreviousRoute: 'previousRoute',
      }),
    },
    computed: {
      isOnboarding() {
        if (this.$route.name === 'Language') return this.$route.query.setup === 'true'
        return this.$route.name === 'Setup' && !this.$route.query.focus &&
          this.launchSetupGroups.some(group => group.id === this.$route.params.group)
      },
      showNext() {
        return this.isOnboarding || this.ready
      },
      content() {
        if (this.isOnboarding) return 'step'
        let content = 'text' // text is for views like the preference summary

        switch (this.$route.name) {
          case 'Setup':
          case 'Preference':
            content = 'step'; // step is for engagement preferences
            break;
          default:
            break;
        }

        return content;
      },
      steps() {
        if (this.isOnboarding) {
          const currentStep = this.$route.name === 'Language'
            ? 'language'
            : this.$route.params.group
          return this.launchSetupSteps.map(step => step.id === currentStep)
        }
        if (this.$route.name === 'Setup') {
          const currentIndex = this.launchSetupGroups.findIndex(
            group => group.id === this.$route.params.group,
          )
          const steps = new Array(this.launchSetupGroups.length).fill(false)
          if (currentIndex >= 0) steps[currentIndex] = true
          return steps
        }

        // returns an array of values for dots, where true indicates it is selected
        let arr = new Array(this.prefOrder.total).fill(false)
        arr[this.prefOrder.ind] = true
        return arr
      },
      barText() {
        // placeholder - returns text for bottom bar
        let key = '';

        switch(this.$route.name) {
          case 'Summary':
            key = 'app.summary.user.title';
            break;
          case 'Language':
            key = 'app.nav.menu.language.title';
            break;
          case 'Location':
            key = 'app.summary.location.title';
            break;
          case 'UserInterface':
              key = 'app.settings.user_interface.title';
              break;
          case 'Settings':
              key = 'app.settings.title';
              break;
          default:
            break;
        }

        return key;
      },
      ready() {
        if (this.$route.name === 'Setup') {
          return Boolean(this.$route.params.group) &&
            this.launchSetupGroupComplete(this.$route.params.group)
        }
        return this.navigationReady
      },
      hasBarContent() {
        return this.content !== 'text' || Boolean(this.barText)
      },
      showFooter() {
        return this.showPrevious || this.ready || this.hasBarContent
      },
      showPrevious() {
        if (this.isOnboarding) {
          return this.$route.name === 'Setup' && Boolean(this.launchSetupPreviousRoute(
            this.$route.params.group,
            { includeLanguage: true },
          ))
        }
        if (this.$route.name === 'Setup') {
          return this.launchSetupGroups.findIndex(
            group => group.id === this.$route.params.group,
          ) > 0
        }
        return this.navigationShowPrevious
      },
      ...mapPiniaState(usePreferenceStore, ['getOrder']),
      prefOrder() {
        return this.getOrder(this.$route.params.panels_name)
      },
      ...mapPiniaState(useLaunchSetupStore, {
        launchSetupGroups: 'groups',
        launchSetupSteps: 'onboardingSteps',
        launchSetupGroupComplete: 'isGroupComplete',
      }),
    }
  }
</script>
<style type="text/css">
  .progress-card {
    position: absolute;
    left: 50%;
    transform: translate(-50%, 0);
    bottom: 20px;
    font-size: 18px;
    font-weight: 600;
    max-width: inherit;
    width: 90%;
    z-index: 5;
  }
  .progress-card__row {
    min-height: 44px;
  }
  .progress-dot {
    height: 10px;
    width: 10px;
    border-radius: 50%;
  }
  .faded {
    opacity: 0.3;
  }
</style>
